import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Calendar, Image as ImageIcon, UserCheck, Clock, TrendingUp, BarChart3 } from 'lucide-react';
import adhesionsService, { AdhesionStatistics } from '../../services/adhesions.service';
import { EvenementsService } from '../../services/evenements.service';
import api from '../../services/api';

interface DashboardStats {
  adhesions: AdhesionStatistics;
  evenements: {
    total: number;
    upcoming: number;
    ongoing: number;
    past: number;
  };
  albums: {
    total: number;
    totalImages: number;
  };
}

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      setLoading(true);

      // Fetch adhesion statistics
      const adhesionStats = await adhesionsService.getStatistics();

      // Fetch all events to calculate statistics
      const eventsResponse = await EvenementsService.findAll(1, 1000);
      const events = Array.isArray(eventsResponse) ? eventsResponse : eventsResponse.data;

      const now = new Date();
      const upcoming = events.filter(e => new Date(e.dateDebut) > now).length;
      const ongoing = events.filter(e => new Date(e.dateDebut) <= now && new Date(e.dateFin) >= now).length;
      const past = events.filter(e => new Date(e.dateFin) < now).length;

      // Fetch albums statistics (without pagination to avoid skip error)
      const albumsResponse = await api.get('/albums');
      const albumsData = albumsResponse.data;
      const albums = Array.isArray(albumsData) ? albumsData : albumsData.data || [];
      const totalImages = albums.reduce((sum: number, album: any) => sum + (album.images?.length || 0), 0);

      setStats({
        adhesions: adhesionStats,
        evenements: {
          total: events.length,
          upcoming,
          ongoing,
          past,
        },
        albums: {
          total: albums.length,
          totalImages,
        },
      });
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
      },
    },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-500">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">Erreur lors du chargement des statistiques</p>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Adhérents',
      value: stats.adhesions.total,
      subtitle: `${stats.adhesions.approved} approuvés`,
      icon: Users,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      gradient: 'from-primary/20 to-primary/5',
    },
    {
      title: 'Adhésions en attente',
      value: stats.adhesions.pending,
      subtitle: 'À traiter',
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      gradient: 'from-yellow-100 to-yellow-50',
    },
    {
      title: 'Événements',
      value: stats.evenements.total,
      subtitle: `${stats.evenements.upcoming} à venir`,
      icon: Calendar,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      gradient: 'from-blue-100 to-blue-50',
    },
    {
      title: 'Albums Photos',
      value: stats.albums.total,
      subtitle: `${stats.albums.totalImages} images`,
      icon: ImageIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      gradient: 'from-purple-100 to-purple-50',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
            <BarChart3 className="w-10 h-10 text-primary" />
            Tableau de bord
          </h1>
          <p className="text-gray-500 mt-2 text-lg">Vue d'ensemble de votre plateforme SAPI</p>
        </div>
        <button
          onClick={loadStatistics}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
        >
          Actualiser
        </button>
      </div>

      {/* Stats Grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={index}
              variants={itemVariants}
              className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 group"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
              <div className="relative p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`${stat.bgColor} p-3 rounded-xl group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">{stat.title}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    {stat.subtitle}
                  </p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Detailed Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Adhesions Breakdown */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-primary/10 p-2 rounded-lg">
              <UserCheck className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Statut des Adhésions</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-100">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="font-medium text-gray-700">Approuvées</span>
              </div>
              <span className="text-2xl font-bold text-green-600">{stats.adhesions.approved}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-xl border border-yellow-100">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="font-medium text-gray-700">En attente</span>
              </div>
              <span className="text-2xl font-bold text-yellow-600">{stats.adhesions.pending}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-100">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="font-medium text-gray-700">Rejetées</span>
              </div>
              <span className="text-2xl font-bold text-red-600">{stats.adhesions.rejected}</span>
            </div>
          </div>
        </motion.div>

        {/* Events Breakdown */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-50 p-2 rounded-lg">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Statut des Événements</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-100">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="font-medium text-gray-700">À venir</span>
              </div>
              <span className="text-2xl font-bold text-blue-600">{stats.evenements.upcoming}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-100">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="font-medium text-gray-700">En cours</span>
              </div>
              <span className="text-2xl font-bold text-green-600">{stats.evenements.ongoing}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                <span className="font-medium text-gray-700">Passés</span>
              </div>
              <span className="text-2xl font-bold text-gray-600">{stats.evenements.past}</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage;
